import { z } from 'zod';
import { fieldValueKind, isContentField } from '../types/fields.js';
import { emailSchema, trimmedString } from './common.js';
// ---------------------------------------------------------------------------
// Respondent identity
// ---------------------------------------------------------------------------
export const respondentIdentitySchema = z.object({
    firstName: trimmedString(1, 100),
    lastName: trimmedString(1, 100),
    email: emailSchema,
});
const asOptionValues = (config) => {
    const options = config.options;
    if (!Array.isArray(options))
        return [];
    return options
        .map((o) => (o && typeof o === 'object' ? o.value : undefined))
        .filter((v) => typeof v === 'string');
};
const num = (config, key) => {
    const value = config[key];
    return typeof value === 'number' ? value : undefined;
};
/** Builds the zod schema for a single answer, given the stored field. */
export function buildAnswerSchema(field, options) {
    const required = options.enforceRequired && field.required;
    const kind = fieldValueKind(field.type);
    switch (kind) {
        case 'none':
        case 'signature':
            // Content blocks carry no answer; signatures are uploaded through their
            // own endpoint so the image never travels inside a JSON answer map.
            return z.undefined();
        case 'text': {
            let schema;
            if (field.type === 'EMAIL') {
                schema = z
                    .string()
                    .transform((v) => v.trim())
                    .pipe(required ? z.email().max(254) : z.union([z.literal(''), z.email().max(254)]));
            }
            else if (field.type === 'SELECT' || field.type === 'RADIO') {
                const values = asOptionValues(field.config);
                const allowed = new Set(values);
                schema = z
                    .string()
                    .refine((v) => (v === '' && !required) || allowed.has(v), 'Seleccione una de las opciones');
            }
            else {
                const maxLength = num(field.config, 'maxLength') ?? (field.type === 'INITIALS' ? 10 : 500);
                const minLength = num(field.config, 'minLength');
                schema = z
                    .string()
                    .transform((v) => v.trim())
                    .pipe(z
                    .string()
                    .max(maxLength)
                    .refine((v) => v === '' || minLength === undefined || v.length >= minLength, minLength
                    ? `Escriba al menos ${minLength} caracteres`
                    : 'El valor es demasiado corto'));
            }
            const withRequirement = required
                ? schema.refine((v) => v.trim().length > 0, 'Este campo es obligatorio')
                : schema;
            return required ? withRequirement : withRequirement.optional().nullable();
        }
        case 'number': {
            let schema = z.coerce.number();
            const min = num(field.config, 'min');
            const max = num(field.config, 'max');
            if (min !== undefined)
                schema = schema.min(min);
            if (max !== undefined)
                schema = schema.max(max);
            return required ? schema : schema.optional().nullable();
        }
        case 'date': {
            const schema = z.iso.date();
            return required
                ? schema
                : z
                    .union([z.literal(''), schema])
                    .optional()
                    .nullable();
        }
        case 'boolean': {
            if (required) {
                // An acceptance that is required is only satisfied by an explicit yes.
                return z.literal(true, {
                    error: 'Debe aceptar esto para continuar',
                });
            }
            return z.boolean().optional().nullable();
        }
        case 'list': {
            const values = asOptionValues(field.config);
            const allowed = new Set(values);
            const minSelected = num(field.config, 'minSelected') ?? (required ? 1 : 0);
            const maxSelected = num(field.config, 'maxSelected');
            let schema = z
                .array(z.string().refine((v) => allowed.has(v), 'Opción no válida'))
                .max(values.length);
            if (required || minSelected > 0)
                schema = schema.min(minSelected);
            if (maxSelected !== undefined)
                schema = schema.max(maxSelected);
            return required ? schema : schema.optional().nullable();
        }
        default: {
            const exhaustive = kind;
            throw new Error(`Unhandled field value kind: ${String(exhaustive)}`);
        }
    }
}
/**
 * Builds the schema for the whole answer map of a form version.
 *
 * Unknown keys are stripped rather than rejected so that a respondent with a
 * stale tab cannot fail a submission because a draft field was renamed; the
 * authoritative field list is always the stored version.
 */
export function buildAnswersSchema(fields, options) {
    const shape = {};
    for (const field of fields) {
        if (isContentField(field.type) || fieldValueKind(field.type) === 'signature')
            continue;
        const schema = buildAnswerSchema(field, options);
        shape[field.key] = options.enforceRequired && field.required ? schema : schema.optional();
    }
    return z.object(shape).strip();
}
// ---------------------------------------------------------------------------
// Public submission payloads
// ---------------------------------------------------------------------------
/** Free-form answer map; each value is validated against its stored field. */
export const answerMapSchema = z.record(z.string().max(64), z.unknown());
export const saveAnswersSchema = z.object({
    answers: answerMapSchema,
});
/** A drawn signature, sent as a PNG data URL produced by the signature pad. */
export const signatureUploadSchema = z.object({
    fieldKey: z.string().max(64),
    /** `data:image/png;base64,....` - the byte size limit is enforced by the API. */
    dataUrl: z
        .string()
        .max(4_000_000)
        .regex(/^data:image\/png;base64,[A-Za-z0-9+/=\s]+$/, 'La firma debe ser una imagen PNG'),
});
export const completeSubmissionSchema = z.object({
    answers: answerMapSchema.optional(),
});
//# sourceMappingURL=submission.js.map